import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { useDataProvider } from 'react-admin';
import { useTenant } from '../contexts/TenantContext';

interface ServerBrandConfig {
  id?: string;
  tenantId: string;
  brandName: string;
  logoUrl?: string;
  faviconUrl?: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
  };
  theme: {
    headerGradient: string;
    buttonGradient: string;
    cardGradient?: string;
  };
  customCss?: string;
  fontFamily: string;
}

// API response interface
interface ApiBrandConfig {
  id?: string;
  tenantCode: string;
  brandName: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor?: string;
  headerGradient: string;
  buttonGradient: string;
  cardGradient?: string;
  customCSS?: string;
  fontFamily: string;
}

interface BrandContextType {
  brand: ServerBrandConfig | null;
  loading: boolean;
  error: string | null;
  updateBranding: (updates: Partial<ServerBrandConfig>) => Promise<void>;
  refreshBranding: () => Promise<void>;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a ServerBrandProvider');
  }
  return context;
};

// Transform API response to internal format
const transformApiBranding = (apiBranding: ApiBrandConfig): ServerBrandConfig => ({
  id: apiBranding.id,
  tenantId: apiBranding.tenantCode,
  brandName: apiBranding.brandName,
  logoUrl: apiBranding.logoUrl,
  faviconUrl: apiBranding.faviconUrl,
  colors: {
    primary: apiBranding.primaryColor,
    secondary: apiBranding.secondaryColor,
    accent: apiBranding.accentColor,
    background: apiBranding.backgroundColor,
  },
  theme: {
    headerGradient: apiBranding.headerGradient,
    buttonGradient: apiBranding.buttonGradient,
    cardGradient: apiBranding.cardGradient,
  },
  customCss: apiBranding.customCSS,
  fontFamily: apiBranding.fontFamily,
});

// Default branding fallback
const getDefaultBranding = (tenantId: string): ServerBrandConfig => {
  // Tenant-specific default configurations
  if (tenantId === 'dt') {
    return {
      tenantId,
      brandName: 'Distill Pest Control',
      logoUrl: '/distill-logo.png', // You can add a specific logo like '/dt-logo.png' if available
      colors: {
        primary: '#16a34a', // Green for pest control
        secondary: '#15803d',
        accent: '#86efac',
        background: '#F5F7FA',
      },
      theme: {
        headerGradient: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
        buttonGradient: 'linear-gradient(45deg, #86efac 30%, #4ade80 90%)',
      },
      fontFamily: 'Roboto',
    };
  }
  
  // Default Distill Mobile branding
  return {
    tenantId,
    brandName: 'Distill Mobile',
    logoUrl: '/distill-logo.png',
    colors: {
      primary: '#667eea',
      secondary: '#764ba2',
      accent: '#FE6B8B',
      background: '#F5F7FA',
    },
    theme: {
      headerGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      buttonGradient: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
    },
    fontFamily: 'Roboto',
  };
};

// Apply branding to DOM
const applyBrandingToDOM = (branding: ServerBrandConfig) => {
  const root = document.documentElement;
  
  // Apply CSS custom properties
  root.style.setProperty('--brand-primary', branding.colors.primary);
  root.style.setProperty('--brand-secondary', branding.colors.secondary);
  root.style.setProperty('--brand-accent', branding.colors.accent);
  root.style.setProperty('--brand-background', branding.colors.background);
  root.style.setProperty('--brand-gradient', branding.theme.headerGradient);
  root.style.setProperty('--brand-button-gradient', branding.theme.buttonGradient);
  
  // Apply card gradient if provided
  if (branding.theme.cardGradient) {
    root.style.setProperty('--brand-card-gradient', branding.theme.cardGradient);
  }

  // Prefer server/tenant branding for white-label; env title is the static shell fallback (see index.html)
  document.title = branding.brandName || import.meta.env.VITE_APP_TITLE || 'DistillHQ';
  
  // Update favicon if provided
  if (branding.faviconUrl) {
    let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = branding.faviconUrl;
  }
  
  // Apply custom CSS if provided
  if (branding.customCss) {
    let customStyle = document.getElementById('tenant-custom-css');
    if (!customStyle) {
      customStyle = document.createElement('style');
      customStyle.id = 'tenant-custom-css';
      document.head.appendChild(customStyle);
    }
    customStyle.textContent = branding.customCss;
  }

  // Apply font family
  root.style.setProperty('--brand-font-family', branding.fontFamily);
  document.body.style.fontFamily = branding.fontFamily;
};

interface ServerBrandProviderProps {
  children: ReactNode;
}

export const ServerBrandProvider: React.FC<ServerBrandProviderProps> = ({ children }) => {
  const { currentTenant, loading: tenantLoading } = useTenant();
  const dataProvider = useDataProvider();
  const [brandConfig, setBrandConfig] = useState<ServerBrandConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = React.useRef(false);

  const loadBranding = useCallback(async () => {
    if (loadingRef.current) {
      return; // Already loading, prevent duplicate requests
    }
    
    if (!currentTenant?.code) {
      // Use default branding if no tenant
      const defaultBranding = getDefaultBranding('default');
      setBrandConfig(defaultBranding);
      applyBrandingToDOM(defaultBranding);
      setLoading(false);
      return;
    }
    
    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      // Always try to fetch tenant branding from server
      const response = await dataProvider.getOne('branding/tenants', { id: currentTenant.code });
      
      if (response.data) {
        const transformedBranding = transformApiBranding(response.data);
        setBrandConfig(transformedBranding);
        applyBrandingToDOM(transformedBranding);
      } else {
        // Use default branding if none configured
        const defaultBranding = getDefaultBranding(currentTenant.code);
        setBrandConfig(defaultBranding);
        applyBrandingToDOM(defaultBranding);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load tenant branding, using default:', err);
      const defaultBranding = getDefaultBranding(currentTenant?.code || 'default');
      setBrandConfig(defaultBranding);
      applyBrandingToDOM(defaultBranding);
      setError('Failed to load branding configuration');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [currentTenant, dataProvider]);

  const updateBranding = async (updates: Partial<ServerBrandConfig>) => {
    if (!currentTenant?.code || !brandConfig) {
      return;
    }
    
    try {
      setError(null);
      
      // Merge updates with existing config
      const updatedBranding = { ...brandConfig, ...updates };
      
      // Transform to API format
      const apiData: Partial<ApiBrandConfig> = {
        brandName: updatedBranding.brandName,
        logoUrl: updatedBranding.logoUrl,
        faviconUrl: updatedBranding.faviconUrl,
        primaryColor: updatedBranding.colors?.primary || brandConfig.colors.primary,
        secondaryColor: updatedBranding.colors?.secondary || brandConfig.colors.secondary,
        accentColor: updatedBranding.colors?.accent || brandConfig.colors.accent,
        backgroundColor: updatedBranding.colors?.background || brandConfig.colors.background,
        headerGradient: updatedBranding.theme?.headerGradient || brandConfig.theme.headerGradient,
        buttonGradient: updatedBranding.theme?.buttonGradient || brandConfig.theme.buttonGradient,
        cardGradient: updatedBranding.theme?.cardGradient,
        customCSS: updatedBranding.customCss,
        fontFamily: updatedBranding.fontFamily,
      };
      
      // Use custom fetch for PUT request
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      
      const response = await fetch(`${baseUrl}/branding/tenants/${currentTenant.code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tenant-code': currentTenant.code,
        },
        body: JSON.stringify(apiData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update branding');
      }
      
      const responseData = await response.json();
      if (responseData) {
        const transformedBranding = transformApiBranding(responseData);
        setBrandConfig(transformedBranding);
        applyBrandingToDOM(transformedBranding);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to update branding:', err);
      setError('Failed to update branding configuration');
      throw err;
    }
  };

  const refreshBranding = async () => {
    await loadBranding();
  };

  useEffect(() => {
    if (!tenantLoading && currentTenant) {
      loadBranding();
    } else if (!tenantLoading && !currentTenant) {
      // No tenant available, use default branding
      const defaultBranding = getDefaultBranding('default');
      setBrandConfig(defaultBranding);
      applyBrandingToDOM(defaultBranding);
      setLoading(false);
    }
  }, [currentTenant, tenantLoading, loadBranding]);

  return (
    <BrandContext.Provider value={{ 
      brand: brandConfig, 
      loading, 
      error, 
      updateBranding, 
      refreshBranding 
    }}>
      {children}
    </BrandContext.Provider>
  );
};